import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/util/Reveal";

/**
 * Shared product card — single source of truth for every "purchasable
 * package" surface across the site (homepage Services teaser, /services
 * page, homepage Creator Setups teaser, /laptops page). Structural fix
 * for the drift that kept building up when each page rendered its own
 * near-duplicate card.
 *
 * The card layout is fixed (image-on-top, content-below). Variants only
 * change WHICH pieces of content render, never the surrounding shell —
 * that guarantees the two teaser variants and the two "full" variants
 * cannot diverge structurally.
 *
 * Props:
 *   image           string — main card image (required)
 *   imageAspect     "16/9" | "4/3" — defaults to 16/9
 *   imageClassName  string — extra classes for the img itself (e.g.
 *                   "object-contain p-6" for laptop cutouts on transparent
 *                   backgrounds vs. the default cover for photos).
 *   icon            string — Lucide icon name (renders as floating badge)
 *   iconUrl         string — external icon URL (used instead of `icon`
 *                   when set; both render in the same floating badge slot,
 *                   so the two collections look aligned).
 *   name            string — card title (required)
 *   byline          string — optional bold audience/context line rendered
 *                   directly under the title, above the description. Muted
 *                   relative to the title. Nothing renders (no gap) when unset.
 *   description     string — short description under title
 *   checklist       string[] — optional bullet list (2-col grid, capped at 6)
 *   price           number — optional price value (renders price block)
 *   priceLabel      string — e.g. "Starting At" (default "Starting At")
 *   priceFormatted  string — pre-formatted price string, e.g. "AWG 1,799"
 *   priceSub        string — small sub line under price, e.g. "≈ $1,005 USD"
 *   primaryCta      { label, to, onClick, testId }
 *   secondaryCta    { label, to, testId }
 *   testId          string — data-testid for the card <article>
 *   reveal          object|false — controls the entrance animation.
 *                   • Default: enabled with subtle variant + delay 0.
 *                     Every ProductCard gets scroll-reveal automatically
 *                     so a page can't forget to add motion and end up
 *                     with static cards while every sibling page has
 *                     animated ones (see Phase 8.1.3 root cause).
 *                   • `{ delay, variant, ...revealProps }` overrides.
 *                     Callers in a grid should pass `{ delay: i * 70 }`
 *                     so cards cascade with the loop index.
 *                   • `false` opts out entirely (e.g. admin previews,
 *                     already-visible modal content).
 */
export const ProductCard = ({
  image,
  imageAspect = "16/9",
  imageClassName = "w-full h-full object-cover",
  icon,
  iconUrl,
  name,
  byline,
  description,
  checklist,
  checklistLabel,  // optional caption rendered above the checklist (e.g. "Includes:") — Workstations page uses this so items read as included products rather than capabilities.
  price,
  priceLabel = "Starting At",
  priceFormatted,
  priceSub,
  primaryCta,
  secondaryCta,
  testId,
  titleClassName,
  reveal = {},
  compact = false,   // Home Workstations showcase variant (Sep 2026 refinement):
                     // image-forward 3/2 artwork window (~54% of card height),
                     // understated 1px low-contrast border + restrained radius
                     // (no lift, no hover shadow, no bright outline), roomier
                     // padding. Used ONLY by the homepage LaptopSetups grid.
  editorialNumber,   // optional 1-based collection index (compact only):
                     // compact numbered badge (01/02/03/04 on dark,
                     // thin orange border) in the upper-left corner of the
                     // IMAGE — the former category-icon slot. Absolute:
                     // zero effect on card height, alignment or data flow.
  editorialAccent,   // optional hex color for the numeral only (category
                     // accent marker). Badge shell stays dark + orange
                     // outline regardless. Defaults to Ur Designs orange.
  hoverImage,        // optional dashboard-controlled `image_alt` URL
                     // (compact only): "Workstation Shift" — reveals over
                     // the primary image via a soft left→right clip wipe
                     // on desktop hover. Absent = primary-only micro-zoom
                     // fallback. Never hard-code paths here.
}) => {
  const Icon = icon ? (Icons[icon] || Icons.Sparkles) : null;
  const aspectClass = compact
    ? "aspect-[3/2]"                                      // Home Workstations: artwork is the hero (~54% of the card height), object-cover with the laptop as focal point
    : imageAspect === "4/3" ? "aspect-[4/3]" : "aspect-[16/9]";

  const article = (
    <article
      data-testid={testId}
      className={`min-w-0 h-full group bg-[#141414] text-white overflow-hidden border flex flex-col ${
        compact
          ? "rounded-xl border-[#1E1E1E] hover:border-[#333333] transition-colors"
          : "rounded-2xl border-[#262626] hover:border-[#FF6600]/40 ur-card-lift"
      }`}
    >
      <div className={`relative ${aspectClass} overflow-hidden bg-[#0F0F0F]`}>
        <img src={image} alt={name} className={`${imageClassName}${compact ? " ws-shift-base" : ""}`} />
        {compact && hoverImage && (
          <div className="ws-shift-alt" aria-hidden="true" data-testid={`${testId}-shift`}>
            <img src={hoverImage} alt="" className={imageClassName} loading="eager" fetchpriority="low" decoding="async" />
            <div className="ws-shift-sweep" />
          </div>
        )}
        {compact && editorialNumber != null && (
          <span
            aria-hidden="true"
            data-testid={`ws-editorial-num-${editorialNumber}`}
            style={{ "--ws-accent": editorialAccent || "#FF6600" }}
            className="ws-num-badge absolute top-4 left-4 h-[45px] min-w-[45px] px-2.5 rounded-lg bg-[#0F0F0F] border grid place-items-center font-display font-black text-[18px] leading-none select-none pointer-events-none"
          >
            {String(editorialNumber).padStart(2, "0")}
          </span>
        )}
        {(Icon || iconUrl) && (
          <div className="absolute top-4 left-4 w-12 h-12 rounded-lg bg-[#0F0F0F] border border-[#FF6600]/40 grid place-items-center">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt=""
                className="w-7 h-7 object-contain"
                style={{ filter: "drop-shadow(0 0 6px rgba(255,102,0,0.35))" }}
              />
            ) : (
              <Icon className="w-6 h-6 text-[#FF6600]" />
            )}
          </div>
        )}
      </div>

      <div className={`${compact ? "p-4" : "p-5"} min-w-0 flex-1 flex flex-col`}>
        <h3 className={`font-display leading-tight font-bold break-words ${titleClassName || (compact ? "text-[16px]" : "text-[19px]")}`}>{name}</h3>
        {(byline?.text || (typeof byline === "string" && byline)) && (
          <div className={bylineClasses(byline)}>
            {typeof byline === "string" ? byline : byline.text}
          </div>
        )}
        {description && (
          <p className="mt-1.5 text-[13px] text-[#A3A3A3] leading-snug break-words">
            {description}
          </p>
        )}

        {checklist && checklist.length > 0 && (
          <div className="mt-3.5">
            {checklistLabel && (
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A7A7A] mb-1.5">
                {checklistLabel}
              </div>
            )}
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11.5px] text-[#D1D1D1]">
              {checklist.slice(0, 6).map((f, i) => (
                <li key={`${f}-${i}`} className="flex items-start gap-1.5 min-w-0">
                  <Check className="w-3 h-3 text-[#FF6600] mt-0.5 shrink-0" strokeWidth={3} />
                  <span className="truncate">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(price !== undefined || priceFormatted) && (
          <div className={compact ? "mt-3" : "mt-4"}>
            <div className="text-[#FF6600] text-[10px] font-extrabold tracking-[0.2em] uppercase">
              {priceLabel}
            </div>
            <div className={`font-display ${compact ? "text-[17px]" : "text-2xl"} font-black text-[#FF6600] mt-1 leading-none`}>
              {priceFormatted}
            </div>
            {priceSub && <div className="text-[11px] text-[#A3A3A3] mt-1">{priceSub}</div>}
          </div>
        )}

        {(primaryCta || secondaryCta) && (
          <div className={`mt-auto ${compact ? "pt-3" : "pt-4"} flex flex-col gap-2`}>
            {primaryCta && <CardCta variant="primary" compact={compact} {...primaryCta} />}
            {secondaryCta && <CardCta variant="secondary" compact={compact} {...secondaryCta} />}
          </div>
        )}
      </div>
    </article>
  );

  if (reveal === false) return article;
  // Wrap in Reveal by default. Consumers in a grid pass `reveal={{ delay: i*70 }}`
  // for a cascading stagger; individual cards outside a grid can pass `{}` (or omit
  // the prop) for a plain subtle fade-in on scroll.
  return (
    <Reveal className="min-w-0 h-full" {...(reveal || {})}>
      {article}
    </Reveal>
  );
};

// Class builder for the byline block — always muted relative to the title
// (that's a fixed rule regardless of style prefs) but weight, style and size
// are driven by the admin-configured BylineStyle object.
const bylineSizeMap = {
  small: "text-[12px]",
  medium: "text-[13.5px]",
  large: "text-[15px]",
};
const bylineClasses = (b) => {
  if (!b || typeof b === "string") return "mt-1 text-[12px] font-bold text-[#D1D1D1] leading-snug break-words";
  const size = bylineSizeMap[b.size] || bylineSizeMap.small;
  const weight = b.bold === false ? "font-normal" : "font-bold";
  const style = b.italic ? "italic" : "";
  return `mt-1 leading-snug break-words text-[#D1D1D1] ${size} ${weight} ${style}`.trim();
};

const CardCta = ({ variant, label, to, onClick, testId, compact = false }) => {
  const padding = compact ? "px-4 py-3" : "px-3.5 py-2"; // compact: ~40px tall CTA
  const textSize = compact ? "text-[11px]" : "text-[10.5px]";
  // Workstation CTA "stretch" hover (Sep 2026, owner-approved) — compact
  // home cards ONLY: the button itself never scales; its content opens
  // up (label tracking 0.15em → 0.24em, arrow travels 7px right, 300ms
  // ease-out, background only a subtle brightness lift). Non-compact
  // surfaces keep the legacy gap-widen hover untouched.
  const stretch = compact && variant === "primary";
  const cls =
    variant === "primary"
      ? stretch
        ? `ws-cta inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FF6600] text-white font-bold ${textSize} tracking-[0.15em] uppercase ${padding} hover:bg-[#FF7714] transition-colors`
        : `inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FF6600] text-white font-bold ${textSize} tracking-[0.15em] uppercase ${padding} hover:bg-[#E65C00] hover:gap-2.5 transition-all`
      : `inline-flex items-center justify-center gap-1.5 rounded-full border border-[#3A3A3A] text-white ${textSize} font-bold tracking-[0.15em] uppercase ${padding} hover:border-[#FF6600] hover:text-[#FF6600] transition-colors`;
  const inner = (
    <>
      <span className={`truncate${stretch ? " ws-cta-label" : ""}`}>{label}</span> <ArrowRight className={`w-3 h-3 shrink-0${stretch ? " ws-cta-arrow" : ""}`} />
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testId} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={cls}>
      {inner}
    </button>
  );
};
